import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    CookieConsentPrefs,
    OPEN_CONSENT_EVENT,
    getConsentPrefs,
    saveConsentPrefs,
} from '../utils/cookieConsent';
import './CookieConsent.css';

interface Props {
    onConsentChange: (prefs: CookieConsentPrefs) => void;
}

function CookieConsent({ onConsentChange }: Props) {
    const [visible, setVisible] = useState(false);
    const [analytics, setAnalytics] = useState(false);
    const [recaptcha, setRecaptcha] = useState(false);
    const [calendly, setCalendly] = useState(false);

    useEffect(() => {
        const stored = getConsentPrefs();
        if (stored) {
            setAnalytics(stored.analytics);
            setRecaptcha(stored.recaptcha);
            setCalendly(stored.calendly);
            onConsentChange(stored);
        } else {
            setVisible(true);
        }

        const handler = () => {
            const current = getConsentPrefs();
            if (current) {
                setAnalytics(current.analytics);
                setRecaptcha(current.recaptcha);
                setCalendly(current.calendly);
            }
            setVisible(true);
        };
        window.addEventListener(OPEN_CONSENT_EVENT, handler);
        return () => window.removeEventListener(OPEN_CONSENT_EVENT, handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function save(prefs: { analytics: boolean; recaptcha: boolean; calendly: boolean }) {
        const saved = saveConsentPrefs(prefs);
        setAnalytics(prefs.analytics);
        setRecaptcha(prefs.recaptcha);
        setCalendly(prefs.calendly);
        setVisible(false);
        onConsentChange(saved);
    }

    if (!visible) return null;

    return ReactDOM.createPortal(
        <div className="cc-overlay" role="dialog" aria-modal="true" aria-labelledby="cc-title">
            <div className="cc-backdrop" />
            <div className="cc-panel">
                <div className="cc-header">
                    <span className="cc-icon" aria-hidden="true">🍪</span>
                    <h2 className="cc-title" id="cc-title">Cookie Preferences</h2>
                    <button
                        className="cc-close"
                        aria-label="Close without saving"
                        onClick={() => setVisible(false)}
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                <div className="cc-body">
                    <p className="cc-intro">
                        We use third-party services to improve your experience and secure our forms.
                        Choose which services you'd like to enable — you can change this at any time.
                    </p>

                    <div className="cc-service cc-service--essential">
                        <div className="cc-service-info">
                            <i className="bi bi-shield-check cc-service-icon" />
                            <div>
                                <span className="cc-service-name">Essential Cookies</span>
                                <span className="cc-service-desc">Required for the site to function correctly.</span>
                            </div>
                        </div>
                        <span className="cc-badge-always">Always on</span>
                    </div>

                    <div className="cc-service">
                        <div className="cc-service-info">
                            <i className="bi bi-bar-chart-line cc-service-icon" />
                            <div>
                                <span className="cc-service-name">Google Analytics</span>
                                <span className="cc-service-desc">Helps us understand how visitors use our site (page views, traffic sources).</span>
                            </div>
                        </div>
                        <div className="form-check form-switch cc-toggle">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="cc-analytics"
                                checked={analytics}
                                onChange={e => setAnalytics(e.target.checked)}
                            />
                            <label className="form-check-label visually-hidden" htmlFor="cc-analytics">
                                Enable Google Analytics
                            </label>
                        </div>
                    </div>

                    <div className="cc-service">
                        <div className="cc-service-info">
                            <i className="bi bi-patch-check cc-service-icon" />
                            <div>
                                <span className="cc-service-name">Google reCAPTCHA</span>
                                <span className="cc-service-desc">Protects our contact form from spam and automated abuse.</span>
                            </div>
                        </div>
                        <div className="form-check form-switch cc-toggle">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="cc-recaptcha"
                                checked={recaptcha}
                                onChange={e => setRecaptcha(e.target.checked)}
                            />
                            <label className="form-check-label visually-hidden" htmlFor="cc-recaptcha">
                                Enable Google reCAPTCHA
                            </label>
                        </div>
                    </div>

                    <div className="cc-service">
                        <div className="cc-service-info">
                            <i className="bi bi-calendar-check cc-service-icon" />
                            <div>
                                <span className="cc-service-name">Calendly</span>
                                <span className="cc-service-desc">Enables the appointment scheduling widget for booking sessions.</span>
                            </div>
                        </div>
                        <div className="form-check form-switch cc-toggle">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="cc-calendly"
                                checked={calendly}
                                onChange={e => setCalendly(e.target.checked)}
                            />
                            <label className="form-check-label visually-hidden" htmlFor="cc-calendly">
                                Enable Calendly
                            </label>
                        </div>
                    </div>
                </div>

                <div className="cc-footer">
                    <button
                        className="cc-btn cc-btn-outline"
                        onClick={() => save({ analytics: false, recaptcha: false, calendly: false })}
                    >
                        Decline All
                    </button>
                    <button
                        className="cc-btn cc-btn-secondary"
                        onClick={() => save({ analytics, recaptcha, calendly })}
                    >
                        Save Preferences
                    </button>
                    <button
                        className="cc-btn cc-btn-primary"
                        onClick={() => save({ analytics: true, recaptcha: true, calendly: true })}
                    >
                        Accept All
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default CookieConsent;
